/**
 * ag-reassurance-bar.js
 * Web component : dots via IntersectionObserver, autoplay optionnel
 */

class AgReassuranceBar extends HTMLElement {
  constructor() {
    super();
    this.track = null;
    this.items = [];
    this.dots = [];
    this.dotsContainer = null;
    this.observer = null;
    this.autoplayTimer = null;
    this.currentIndex = 0;
  }

  connectedCallback() {
    this.track = this.querySelector('.ag-reassurance-bar__track');
    this.items = [...this.querySelectorAll('.ag-reassurance-bar__item')];
    this.dotsContainer = this.querySelector('.ag-reassurance-bar__dots');
    this.dots = [...this.querySelectorAll('.ag-reassurance-bar__dot')];

    if (!this.track || this.items.length < 2) return;

    this.initObserver();
    this.initDots();
    this.initAutoplay();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    this.stopAutoplay();
  }

  initObserver() {
    const options = {
      root: this.track,
      threshold: 0.5,
    };

    this.observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          const index = this.items.indexOf(entry.target);
          if (index !== -1) this.setActive(index);
        }
      }
    }, options);

    for (const item of this.items) {
      this.observer.observe(item);
    }
  }

  initDots() {
    for (const dot of this.dots) {
      dot.addEventListener('click', () => {
        const index = parseInt(dot.dataset.index, 10);
        this.scrollTo(index);
        this.resetAutoplay();
      });
    }
  }

  setActive(index) {
    this.currentIndex = index;
    for (let i = 0; i < this.dots.length; i++) {
      this.dots[i].setAttribute('aria-current', i === index ? 'true' : 'false');
    }
  }

  scrollTo(index) {
    const target = this.items[index];
    if (!target) return;
    this.track.scrollTo({
      left: target.offsetLeft - this.track.offsetLeft,
      behavior: 'smooth',
    });
  }

  initAutoplay() {
    const speed = parseInt(this.dataset.autoplay, 10);
    if (!speed || speed <= 0) return;

    this.autoplaySpeed = speed;
    this.startAutoplay();

    this.track.addEventListener('pointerenter', () => this.stopAutoplay());
    this.track.addEventListener('pointerleave', () => this.startAutoplay());
  }

  startAutoplay() {
    if (!this.autoplaySpeed) return;
    this.autoplayTimer = setInterval(() => {
      const next = (this.currentIndex + 1) % this.items.length;
      this.scrollTo(next);
    }, this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  resetAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }
}

if (!customElements.get('ag-reassurance-bar')) {
  customElements.define('ag-reassurance-bar', AgReassuranceBar);
}
